import { useEffect, useState } from "react"
import { Head, Link } from "@inertiajs/react"
import { MessageSquare, Pencil, ArrowRight, CalendarDays } from "lucide-react"
import { OpeningHours } from "./components/OpeningHours"
import { InstagramEmbed } from "./components/InstagramEmbed"
import { BookmarkButton } from "./components/BookmarkButton"
import { ReportDialog } from "./components/ReportDialog"
import { TellManagerDialog } from "./components/TellManagerDialog"
import { WalletButton } from "./components/WalletButton"
import { LoyaltyCardClaim } from "./components/LoyaltyCardClaim"
import { GiftIcon } from "../../components/Icons"
import { LinkThumbnail } from "../../components/LinkThumbnail"
import { COLOR_PRESETS } from "@/data/colorPresets"
import type { CoffeeShopData, ShopEvent } from "./types"

interface ShowPageProps {
  coffee_shop: CoffeeShopData
}

export default function Show({ coffee_shop }: ShowPageProps) {
  const [showReport, setShowReport] = useState(false)
  const [showTellManager, setShowTellManager] = useState(false)
  const [liveOrdering, setLiveOrdering] = useState<{
    slug: string
    acceptsOnlineOrders: boolean
    orderingStatus: CoffeeShopData["ordering_status"]
  } | null>(null)
  const currentOrderingStatus =
    liveOrdering?.slug === coffee_shop.slug
      ? liveOrdering.orderingStatus
      : coffee_shop.ordering_status
  const showOrderButton =
    liveOrdering?.slug === coffee_shop.slug
      ? liveOrdering.acceptsOnlineOrders
      : coffee_shop.show_order_button

  useEffect(() => {
    let controller: AbortController | null = null
    const refresh = () => {
      controller?.abort()
      const requestController = new AbortController()
      controller = requestController

      fetch(
        `/api/v1/coffee_shops/${encodeURIComponent(coffee_shop.slug)}/ordering_status`,
        {
          cache: "no-store",
          signal: requestController.signal,
        }
      )
        .then((response) => {
          if (!response.ok) throw new Error("Ordering status unavailable")
          return response.json()
        })
        .then(
          (status: {
            accepts_online_orders: boolean
            ordering_status: CoffeeShopData["ordering_status"]
          }) => {
            if (
              !requestController.signal.aborted &&
              typeof status.accepts_online_orders === "boolean"
            ) {
              setLiveOrdering({
                slug: coffee_shop.slug,
                acceptsOnlineOrders: status.accepts_online_orders,
                orderingStatus: status.ordering_status,
              })
            }
          }
        )
        .catch(() => {
          // Keep the displayed button when the live read fails.
        })
    }

    const refreshWhenVisible = () => {
      if (document.visibilityState === "visible") refresh()
    }
    const refreshWhenRestored = (event: PageTransitionEvent) => {
      if (event.persisted) refresh()
    }

    refresh()
    window.addEventListener("pageshow", refreshWhenRestored)
    document.addEventListener("visibilitychange", refreshWhenVisible)

    return () => {
      controller?.abort()
      window.removeEventListener("pageshow", refreshWhenRestored)
      document.removeEventListener("visibilitychange", refreshWhenVisible)
    }
  }, [coffee_shop.slug])

  return (
    <>
      <Head title={coffee_shop.name} />

      <div className="flex min-h-full flex-col justify-center px-6 py-12 lg:px-8">
        {coffee_shop.logo_url && (
          <div
            className="fixed inset-0 z-[-1] bg-no-repeat bg-cover bg-center opacity-25"
            style={{
              backgroundImage: `url(${coffee_shop.logo_url})`,
              filter: "blur(50px)",
            }}
          />
        )}

        <div className="relative z-10">
          {/* Logo */}
          <div className="sm:mx-auto sm:w-full sm:max-w-sm">
            {coffee_shop.logo_url && (
              <div className="relative mx-auto max-w-md p-5 flex justify-center items-center">
                <div className="bg-gradient-to-tr from-pink-300 to-blue-300 p-1 rounded-full">
                  <img
                    src={coffee_shop.logo_url}
                    // eslint-disable-next-line no-restricted-syntax -- deliberate bg-white logo plate
                    className="h-24 w-24 rounded-full bg-white p-1"
                    alt={`${coffee_shop.name} logo`}
                    fetchPriority="high"
                  />
                </div>
              </div>
            )}

            {/* Name + Badge */}
            <div className="mt-5 text-center text-2xl font-bold leading-9 tracking-tight text-foreground-primary flex items-center space-x-2 justify-center">
              <span>{coffee_shop.name}</span>
              {coffee_shop.has_owner && (
                <span>
                  <svg
                    className="w-4"
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 512 512"
                    fill="green"
                  >
                    <path d="M256 0C292.8 0 324.8 20.7 340.9 51.1C373.8 40.1 410.1 48.96 437 74.98C463 101 470.1 138.2 460.9 171.1C491.3 187.2 512 219.2 512 256C512 292.8 491.3 324.8 460.9 340.9C471 373.8 463 410.1 437 437C410.1 463 373.8 470.1 340.9 460.9C324.8 491.3 292.8 512 256 512C219.2 512 187.2 491.3 171.1 460.9C138.2 471 101 463 74.98 437C48.96 410.1 41 373.8 51.1 340.9C20.7 324.8 0 292.8 0 256C0 219.2 20.7 187.2 51.1 171.1C40.1 138.2 48.96 101 74.98 74.98C101 48.96 138.2 41 171.1 51.1C187.2 20.7 219.2 0 256 0V0zM352.1 224.1C362.3 215.6 362.3 200.4 352.1 191C343.6 181.7 328.4 181.7 319 191L224 286.1L184.1 247C175.6 237.7 160.4 237.7 151 247C141.7 256.4 141.7 271.6 151 280.1L207 336.1C216.4 346.3 231.6 346.3 240.1 336.1L352.1 224.1z" />
                  </svg>
                </span>
              )}
              {coffee_shop.loyalty_programs?.length > 0 && (
                <span
                  className="inline-flex items-center text-amber-500"
                  title="Has loyalty program"
                  aria-label="Has active loyalty program"
                >
                  <GiftIcon className="w-4 h-4" />
                </span>
              )}
            </div>

            {/* Description */}
            {coffee_shop.description && (
              <div className="mt-1 text-center text-md leading-7 tracking-tight text-foreground-muted flex items-center space-x-2 justify-center">
                {coffee_shop.description}
              </div>
            )}
          </div>

          {/* Seats + Tags */}
          {(coffee_shop.seating_capacity || coffee_shop.tags.length > 0) && (
            <div className="mt-5 sm:mx-auto sm:w-full sm:max-w-sm">
              <div className="flex space-x-2 justify-center flex-wrap -mt-2">
                {coffee_shop.seating_capacity != null && (
                  <div className="mt-2">
                    <span className="inline-flex items-center rounded-full bg-surface-muted px-2 py-1 text-xs font-medium text-foreground-emphasis">
                      {coffee_shop.seating_capacity} seats
                    </span>
                  </div>
                )}
                {coffee_shop.tags.map((tag) => (
                  <a
                    key={tag.id}
                    href={`/?tags[]=${encodeURIComponent(tag.slug)}`}
                  >
                    <div className="mt-2">
                      <span
                        className={`inline-flex items-center rounded-full px-2 py-1 text-xs font-medium ${tag.badge_classes}`}
                      >
                        {tag.name}
                      </span>
                    </div>
                  </a>
                ))}
              </div>
            </div>
          )}

          {coffee_shop.events?.length > 0 && (
            <div className="mt-10 sm:mx-auto sm:w-full sm:max-w-sm">
              <h2 className="mb-3 text-center text-sm font-semibold uppercase tracking-wide text-foreground-muted">
                Happening here
              </h2>
              <div className="flex flex-col space-y-3">
                {coffee_shop.events.map((event) => (
                  <ShopEventCard key={event.slug} event={event} />
                ))}
              </div>
            </div>
          )}

          {/* Instagram Embed */}
          {coffee_shop.instagram_url && (
            <InstagramEmbed instagramUrl={coffee_shop.instagram_url} />
          )}

          {/* Opening Hours */}
          <div className="mt-10 sm:mx-auto sm:w-full sm:max-w-sm">
            <OpeningHours
              openingHoursList={coffee_shop.opening_hours_list}
              openingHoursStatus={coffee_shop.opening_hours_status}
              temporarilyClosed={coffee_shop.temporarily_closed}
            />
          </div>

          {/* Wallet Buttons */}
          {coffee_shop.loyalty_programs.length > 0 &&
            coffee_shop.loyalty_card_generate_url && (
              <div className="mt-10 sm:mx-auto sm:w-full sm:max-w-sm">
                <WalletButton
                  loyaltyPrograms={coffee_shop.loyalty_programs}
                  loyaltyCardGenerateUrl={coffee_shop.loyalty_card_generate_url}
                  walletClaimUrls={coffee_shop.wallet_claim_urls}
                  coffeeShopSlug={coffee_shop.slug}
                  appleAuthUrl={coffee_shop.apple_auth_url}
                  googleAuthUrl={coffee_shop.google_auth_url}
                  googleWalletEnabled={coffee_shop.google_wallet_enabled}
                />
              </div>
            )}

          {/* Loyalty Card Auto-Claim */}
          {coffee_shop.loyalty_programs.length > 0 &&
            coffee_shop.loyalty_card_generate_url && (
              <LoyaltyCardClaim
                loyaltyPrograms={coffee_shop.loyalty_programs}
                loyaltyCardGenerateUrl={coffee_shop.loyalty_card_generate_url}
                appleAuthUrl={coffee_shop.apple_auth_url}
                googleAuthUrl={coffee_shop.google_auth_url}
                coffeeShopSlug={coffee_shop.slug}
              />
            )}

          {/* Group 1: Primary actions — loyalty card + order */}
          {showOrderButton && (
            <div className="mt-6 sm:mx-auto sm:w-full sm:max-w-sm">
              <a
                href={coffee_shop.order_url || `/${coffee_shop.slug}/order`}
                className="block w-full rounded-lg bg-emerald-600 px-3.5 py-4 text-md font-semibold text-white shadow-sm hover:bg-emerald-500 text-center transition-all duration-150 hover:shadow-md active:scale-[0.98]"
              >
                {currentOrderingStatus?.accepting_orders === false
                  ? "View menu"
                  : "Order Online"}
              </a>
              {currentOrderingStatus?.accepting_orders === false && (
                <p className="mt-2 text-center text-sm text-foreground-subtle">
                  {currentOrderingStatus.message ||
                    "Orders are unavailable right now. You can still browse the menu."}
                  {currentOrderingStatus.next_window &&
                    !currentOrderingStatus.paused && (
                      <>
                        {" "}
                        Next ordering window:{" "}
                        {currentOrderingStatus.next_window}.
                      </>
                    )}
                </p>
              )}
            </div>
          )}

          {/* Group 2: Custom links */}
          {coffee_shop.links.length > 0 && (
            <div className="mt-10 sm:mx-auto sm:w-full sm:max-w-sm">
              <div className="flex flex-col space-y-3">
                {coffee_shop.links.map((link) => {
                  const preset = link.color_preset
                    ? (
                        COLOR_PRESETS as Record<
                          string,
                          { bg: string; text: string; border: string }
                        >
                      )[link.color_preset]
                    : null
                  return (
                    <a
                      key={link.name}
                      href={link.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={
                        preset
                          ? ({
                              backgroundColor: preset.bg,
                              color: preset.text,
                              "--tw-ring-color": preset.border,
                            } as React.CSSProperties)
                          : undefined
                      }
                      className="flex items-center space-x-3 rounded-lg bg-card px-3.5 py-3.5 text-md font-semibold text-foreground-secondary shadow-sm ring-1 ring-inset ring-border-strong hover:bg-surface-subtle hover:ring-foreground-faint hover:brightness-95 transition-all duration-150 hover:shadow-md active:scale-[0.98]"
                    >
                      <LinkThumbnail
                        url={link.url}
                        label={link.name}
                        iconName={link.icon_name}
                        iconSvg={link.icon_svg}
                        thumbnailUrl={link.thumbnail_url}
                        className={
                          link.thumbnail_size === "large"
                            ? "h-8 w-8 shrink-0"
                            : "h-5 w-5 shrink-0"
                        }
                        imgClassName={
                          link.thumbnail_size === "large"
                            ? "h-8 w-8 shrink-0 rounded object-cover"
                            : "h-5 w-5 shrink-0 rounded object-cover"
                        }
                      />
                      <span className="flex-1 text-center">{link.name}</span>
                    </a>
                  )
                })}
              </div>
            </div>
          )}

          {/* Group 3: Utility actions — tell manager, bookmark, suggest edit */}
          <div className="mt-10 flex flex-col space-y-3">
            <div className="sm:mx-auto sm:w-full sm:max-w-sm">
              <button
                type="button"
                onClick={() => setShowTellManager(true)}
                className="flex items-center space-x-3 w-full rounded-lg bg-card px-3.5 py-3.5 text-md font-semibold text-foreground-secondary shadow-sm ring-1 ring-inset ring-border-strong hover:bg-surface-subtle hover:ring-foreground-faint transition-all duration-150 hover:shadow-md active:scale-[0.98]"
              >
                <MessageSquare className="h-5 w-5 shrink-0" />
                <span className="flex-1 text-center">Tell Manager</span>
              </button>
            </div>

            <div className="sm:mx-auto sm:w-full sm:max-w-sm">
              <BookmarkButton
                bookmark={coffee_shop.bookmark}
                coffeeShopSlug={coffee_shop.slug}
                coffeeShopId={coffee_shop.coffee_shop_id}
                appleAuthUrl={coffee_shop.apple_auth_url}
                googleAuthUrl={coffee_shop.google_auth_url}
              />
            </div>

            <div className="sm:mx-auto sm:w-full sm:max-w-sm">
              <button
                type="button"
                onClick={() => setShowReport(true)}
                className="flex items-center space-x-3 w-full rounded-lg bg-card px-3.5 py-3.5 text-md font-semibold text-foreground-secondary shadow-sm ring-1 ring-inset ring-border-strong hover:bg-surface-subtle hover:ring-foreground-faint transition-all duration-150 hover:shadow-md active:scale-[0.98]"
              >
                <Pencil className="h-5 w-5 shrink-0" />
                <span className="flex-1 text-center">Suggest Edit</span>
              </button>
            </div>
          </div>

          {/* Browse More */}
          <div className="mt-6 sm:mx-auto sm:w-full sm:max-w-sm">
            <Link
              href={coffee_shop.browse_url}
              className="flex items-center justify-center space-x-2 w-full rounded-lg px-3.5 py-3 text-sm font-semibold text-brand-strong hover:bg-brand-tint/50 transition-all duration-150 active:scale-[0.98]"
            >
              <span>Browse more coffee shops</span>
              <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </div>

      {/* Dialogs */}
      <ReportDialog
        open={showReport}
        onOpenChange={setShowReport}
        reportUrl={coffee_shop.report_url}
      />

      <TellManagerDialog
        open={showTellManager}
        onOpenChange={setShowTellManager}
        tellManagerUrl={coffee_shop.tell_manager_url}
        hasOwnersForFeedback={coffee_shop.has_owners_for_feedback}
      />
    </>
  )
}

function formatEventDates(startsOn: string, endsOn: string) {
  const start = new Date(`${startsOn}T00:00:00`)
  const end = new Date(`${endsOn}T00:00:00`)
  const dateOpts: Intl.DateTimeFormatOptions = {
    day: "numeric",
    month: "short",
  }

  if (startsOn === endsOn) {
    return start.toLocaleDateString("en-MY", { ...dateOpts, year: "numeric" })
  }

  return `${start.toLocaleDateString("en-MY", dateOpts)} – ${end.toLocaleDateString("en-MY", { ...dateOpts, year: "numeric" })}`
}

function ShopEventCard({ event }: { event: ShopEvent }) {
  const dates = formatEventDates(event.starts_on, event.ends_on)
  const content = (
    <>
      {event.image_url ? (
        <img
          src={event.image_url}
          alt=""
          className="h-16 w-12 shrink-0 rounded object-cover"
        />
      ) : (
        <div className="flex h-16 w-12 shrink-0 items-center justify-center rounded bg-surface-muted">
          <CalendarDays className="h-5 w-5 text-foreground-faint" />
        </div>
      )}
      <div className="min-w-0 flex-1">
        <p className="font-semibold text-foreground-primary">{event.name}</p>
        <p className="text-sm text-foreground-muted">{dates}</p>
      </div>
    </>
  )
  const className =
    "flex items-center space-x-3 rounded-lg bg-card px-3.5 py-3 text-left shadow-sm ring-1 ring-inset ring-border-strong"

  return (
    <Link
      href={`/events/${event.slug}`}
      className={`${className} hover:bg-surface-subtle hover:ring-foreground-faint hover:shadow-md transition-all duration-150 active:scale-[0.98]`}
    >
      {content}
    </Link>
  )
}
